import { DEFAULT_MATERIAL_SIZE_UNIT, MATERIAL_SIZE_UNITS, type MaterialSizeUnit, type ProductCreateInput, type ProductDetailDto } from "@hisense/shared";
import { parseWireSize } from "@/domain/wireSize";
import { unitPriceToInput } from "@/domain/money";
import { parseIntField, parseMoneyMajor } from "@/lib/moneyInput";

const moneyToStr = (minor: number | null) => (minor != null ? unitPriceToInput(minor) : "");
const decimalToStr = (value: number | null | undefined) => (value != null ? String(value) : "");
/** Đơn vị lạ (nhập ngoài biểu mẫu) vẫn giữ nguyên để không bị đổi lặng lẽ khi lưu lại; trống thì mm. */
const materialUnit = (unit: string | null | undefined): MaterialSizeUnit | string => unit || DEFAULT_MATERIAL_SIZE_UNIT;

/**
 * Currencies the legacy data actually uses, as a fallback: Settings has no
 * `currencies` option list yet, and a product must still be enterable as RMB/VND.
 * A configured list (optionLists.currencies) wins when one is added.
 */
export const FALLBACK_CURRENCIES = ["USD", "RMB", "VND", "EUR"];

/** Every row in production stores grams; there is no weight-unit option list yet. */
export const DEFAULT_WEIGHT_UNIT = "g";

/**
 * Legacy create defaults (ProductData.js): they are why ~97% of the migrated rows
 * carry an up-charge and a lead time. Edit mode never applies them.
 */
const CREATE_DEFAULTS = {
  moq: "3000",
  upCharge: "45",
  sampleLeadTime: "10",
  bulkLeadTime: "14",
};

/**
 * Weight per 100 units is REAL, not money and not an integer (production holds
 * 3.3). Same contract as parseMoneyMajor: reject junk, never coerce to 0.
 */
export function parseDecimalField(raw: string): { value?: number; invalid?: boolean } {
  let v = raw.trim().replace(/\s/g, "");
  if (v === "") return {};
  if (v.includes(".") && v.includes(",")) v = v.replace(/,/g, "");
  else if (v.includes(",") && !v.includes(".")) v = v.replace(",", ".");
  if (!/^\d+(\.\d{1,4})?$/.test(v)) return { invalid: true };
  return { value: parseFloat(v) };
}

export const initialProductForm = (product: ProductDetailDto | undefined, currency: string) => ({
  productCode: product?.productCode ?? "",
  description: product?.description ?? "",
  descriptionZh: product?.descriptionZh ?? "",
  subRef: product?.subRef ?? "",
  customerRef: product?.customerRef ?? "",
  type: product?.type ?? "",
  brandName: product?.brandName ?? "",
  color: product?.color ?? "",
  colorZh: product?.colorZh ?? "",
  size: product?.size ?? "",
  unit: product ? (product.unit ?? "") : "gross",
  weightPer100: product?.weightPer100 != null ? String(product.weightPer100) : "",
  weightUnit: product ? (product.weightUnit ?? "") : DEFAULT_WEIGHT_UNIT,
  moq: product ? (product.moq != null ? String(product.moq) : "") : CREATE_DEFAULTS.moq,
  reorderPoint: product?.reorderPoint != null ? String(product.reorderPoint) : "",
  mcq: product?.mcq != null ? String(product.mcq) : "",
  msq: product?.msq != null ? String(product.msq) : "",
  approvalStatus: product?.approvalStatus ?? "Pending",
  currency: product?.currency ?? currency,
  sellingPrice: moneyToStr(product?.sellingPrice ?? null),
  dtdPrice: moneyToStr(product?.dtdPrice ?? null),
  exwPrice: moneyToStr(product?.exwPrice ?? null),
  underMoqPrice: moneyToStr(product?.underMoqPrice ?? null),
  lowestPrice: moneyToStr(product?.lowestPrice ?? null),
  upCharge: product ? moneyToStr(product.upCharge ?? null) : CREATE_DEFAULTS.upCharge,
  costUnitPrice: moneyToStr(product?.costUnitPrice ?? null),
  supplierId: product?.supplierId ?? "",
  supplierProductRef: product?.supplierProductRef ?? "",
  sampleLeadTime: product
    ? (product.sampleLeadTime ?? "")
    : CREATE_DEFAULTS.sampleLeadTime,
  bulkLeadTime: product ? (product.bulkLeadTime ?? "") : CREATE_DEFAULTS.bulkLeadTime,
  storage: product?.storage ?? "",
  location: product?.location ?? "",
  moldRef: product?.moldRef ?? "",
  moldLocation: product?.moldLocation ?? "",
  remarks: product?.remarks ?? "",
  imageUrl: product?.imageUrl ?? "",
  imageKey: product?.imageKey ?? "",
  approvalImageUrl: product?.approvalImageUrl ?? "",
  approvalImageKey: product?.approvalImageKey ?? "",
  wireSize: parseWireSize(product?.wireSize),
  materialType: product?.materialType ?? "",
  materialWidth: decimalToStr(product?.materialWidth),
  materialWidthUnit: materialUnit(product?.materialWidthUnit),
  materialThickness: decimalToStr(product?.materialThickness),
  materialThicknessUnit: materialUnit(product?.materialThicknessUnit),
  materialHardness: product?.materialHardness ?? "",
});

export type ProductForm = ReturnType<typeof initialProductForm>;


/** A family variant inherits specifications, never approval evidence or stock. */
export function duplicateProductForm(source: ProductDetailDto, currency: string): ProductForm {
  return {
    ...initialProductForm(source, currency),
    subRef: "", customerRef: "", approvalStatus: "Pending",
    approvalImageUrl: "", approvalImageKey: "",
  };
}

export function duplicateVariantNeedsIdentity(form: Pick<ProductForm, "productCode" | "subRef">, source: Pick<ProductDetailDto, "productCode" | "subRef">): boolean {
  return form.productCode.trim() === source.productCode.trim() && (!form.subRef.trim() || form.subRef.trim() === (source.subRef ?? "").trim());
}

/** Compare content rather than nested wire-size object identities. */
export function isProductFormDirty(form: ProductForm, baseline: ProductForm): boolean {
  return JSON.stringify(form) !== JSON.stringify(baseline);
}

/** Duplication controls are editor-only and must not contaminate list URLs. */
export function productListSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("fromProduct");
  return params.size ? `?${params.toString()}` : "";
}


export const PRODUCT_MONEY_FIELDS = ["sellingPrice", "lowestPrice", "upCharge", "costUnitPrice", "dtdPrice", "exwPrice", "underMoqPrice"] as const;
export const PRODUCT_QUANTITY_FIELDS = ["moq", "reorderPoint", "mcq", "msq"] as const;
/** Số thập phân không âm (không phải tiền, không phải số nguyên): khối lượng / 100, chiều rộng và độ dày vật liệu. */
export const PRODUCT_DECIMAL_FIELDS = ["weightPer100", "materialWidth", "materialThickness"] as const;
type NumericField = typeof PRODUCT_MONEY_FIELDS[number] | typeof PRODUCT_QUANTITY_FIELDS[number] | typeof PRODUCT_DECIMAL_FIELDS[number];

/** Missing remains missing, explicit clearing becomes null. Stock is never sent: Kho & LOT owns it (18/09/2026). */
export function productNumericPayload(form: ProductForm, baseline: ProductForm, editing: boolean, canSeeCost: boolean): {
  data: Pick<ProductCreateInput, NumericField>;
  invalid: NumericField[];
} {
  const data: Pick<ProductCreateInput, NumericField> = {};
  const invalid: NumericField[] = [];
  for (const key of [...PRODUCT_MONEY_FIELDS, ...PRODUCT_QUANTITY_FIELDS, ...PRODUCT_DECIMAL_FIELDS] as const) {
    if (key === "costUnitPrice" && !canSeeCost) continue;
    // Legacy negatives can be left untouched; a user changing them must enter a valid value.
    if (editing && form[key] === baseline[key]) continue;
    const result = (PRODUCT_MONEY_FIELDS as readonly string[]).includes(key)
      ? (() => { const parsed = parseMoneyMajor(form[key]); return { value: parsed.minor, invalid: parsed.invalid }; })()
      : (PRODUCT_DECIMAL_FIELDS as readonly string[]).includes(key) ? parseDecimalField(form[key]) : parseIntField(form[key]);
    const decimal = (PRODUCT_DECIMAL_FIELDS as readonly string[]).includes(key);
    if (result.invalid || (result.value != null && (!Number.isFinite(result.value) || (!decimal && !Number.isSafeInteger(result.value))))) {
      invalid.push(key);
      continue;
    }
    data[key] = result.value ?? (editing ? null : undefined);
  }
  return { data, invalid };
}


/** Unit defaults belong to creation; unrelated edits must preserve legacy nulls. */
export function productUnitPayload(form: ProductForm, baseline: ProductForm, editing: boolean): Pick<ProductCreateInput, "unit" | "weightUnit"> {
  return {
    ...(!editing || form.unit !== baseline.unit ? { unit: form.unit } : {}),
    ...(form.weightUnit !== baseline.weightUnit || (!editing && form.weightPer100.trim() !== "")
      ? { weightUnit: form.weightUnit }
      : {}),
  };
}


const isMaterialUnit = (unit: string): unit is MaterialSizeUnit => (MATERIAL_SIZE_UNITS as readonly string[]).includes(unit);

/**
 * Chữ của thông tin vật liệu, cùng đơn vị rộng / dày. Đơn vị chỉ đi kèm khi ô số có giá trị (một đơn vị đứng một mình không
 * nói gì, như kích thước dây), và chỉ khi nằm trong danh sách — đơn vị lạ thì để nguyên trên máy chủ thay vì bị từ chối.
 */
export function productMaterialPayload(form: ProductForm): Pick<ProductCreateInput, "materialType" | "materialHardness" | "materialWidthUnit" | "materialThicknessUnit"> {
  return {
    materialType: form.materialType,
    materialHardness: form.materialHardness,
    ...(form.materialWidth.trim() && isMaterialUnit(form.materialWidthUnit) ? { materialWidthUnit: form.materialWidthUnit } : {}),
    ...(form.materialThickness.trim() && isMaterialUnit(form.materialThicknessUnit) ? { materialThicknessUnit: form.materialThicknessUnit } : {}),
  };
}
